<script setup lang="ts">
import { ref, computed } from "vue";
import { watch } from "vue";
import { PopoverTag } from "@/components/common";
import { Option } from "@/types";
import { AssetRow, ReportingGroupMinimalSchema } from "@telegraphio/bapi-types";
import { useGetAssetHistory } from "@/api/mutations";

interface Props {
  customerId: string;
  labelClasses: string;
  selectedAsset?: string;
}
const props = defineProps<Props>();

const getAssetHistoryMutation = useGetAssetHistory();

// Track the user's selected shipment (waybillId)
const shipment = ref<string>("");
const selectedShipment = computed({
  get() {
    return shipment.value;
  },
  set(value: string) {
    shipment.value = value;
    const groups = waybillGroups.value?.get(value) ?? [];
    hiddenRgs.value = groups.map((group) => group.id).join(",");
  },
});
const hiddenRgs = ref<string>("");
// Keep a map of every returned shipment's reporting groups
// waybillId: [rgID1, rgID2]
// Maps waybill_id -> reporting_group_ids
const waybillGroups = ref<Map<string, ReportingGroupMinimalSchema[]>>();

// Options available to the dropdown. Populated on open.
const shipmentOptionList = ref<Option[]>([]);

// All the reporting groups returned from BAPI in the asset history.
// Maps reporting_group_id -> reporting_group_name
const reportingGroups = ref<ReportingGroupMinimalSchema[]>([]);

const selectedShipmentReportingGroups = computed<ReportingGroupMinimalSchema[]>(() => {
  const out = [];
  const groups = waybillGroups.value?.get(shipment.value) ?? [];
  for (const group of groups) {
    const dg = reportingGroups.value?.find((rg) => rg.id === group.id);
    if (dg) out.push(dg);
  }
  return out;
});

async function getShipmentsForAsset() {
  if (!props.selectedAsset) return [];
  const responseData = await getAssetHistoryMutation.mutateAsync({
    customerId: props.customerId,
    equipmentId: props.selectedAsset,
    requestData: {
      page: 1,
      page_size: 10,
      min_date_str: null,
      max_date_str: null,
      origin: null,
      destination: null,
      load_empty_status: null,
      sort_column: null,
      sort_column_direction: null,
    },
  });

  waybillGroups.value = new Map();
  for (const waybill of responseData.data) {
    waybillGroups.value.set(waybill.waybill_id, waybill.reporting_group_objects);
  }

  reportingGroups.value = responseData.report_groups;
  return responseData.data;
}

function optionFormatter(waybill: AssetRow) {
  return { label: `${waybill.waybill_date} - #${waybill.waybill_number}`, value: waybill.waybill_id };
}

async function shipmentsLoader(): Promise<Option[]> {
  const shipments = await getShipmentsForAsset();
  shipmentOptionList.value = shipments.map(optionFormatter);
  return shipmentOptionList.value;
}

async function optionLoader(value: string, cached?: Option) {
  if (!props.selectedAsset) return { label: "No shipments found", value: "" };
  if (cached) {
    return cached;
  }

  const result = await getShipmentsForAsset();
  const found = result.find((shipment) => shipment.waybill_id === value);
  return found ? optionFormatter(found) : { label: "No shipment found.", value: "" };
}

watch(
  () => props.selectedAsset,
  (next, prev) => {
    if (next !== prev) {
      shipment.value = "";
      shipmentOptionList.value = [];
    }
  },
);
</script>

<template>
  <div class="flex grow flex-wrap items-center gap-1.5">
    <FormKit
      v-model="selectedShipment"
      type="dropdown"
      label="Shipment"
      name="waybill_id"
      :classes="{
        label: labelClasses + ' !w-auto',
        outer: 'content-center !mb-0',
        wrapper: 'flex gap-2',
        selection: 'inline whitespace-nowrap',
        inner: 'grow',
      }"
      :disabled="!selectedAsset"
      :options="shipmentsLoader"
      :option-loader="optionLoader"
      loader-icon="loader"
      always-load-on-open
      empty-message="No shipments found."
      placeholder="- -"
    >
      <template #loadMore></template>
    </FormKit>
    <div class="flex shrink grow items-center justify-between gap-2 text-sm font-semibold">
      {{ selectedShipmentReportingGroups.length > 0 ? selectedShipmentReportingGroups[0].name : "" }}
      <PopoverTag
        v-if="selectedShipmentReportingGroups.length > 1"
        name="groups"
        :label="`+ ${selectedShipmentReportingGroups.length - 1}`"
      >
        <ul class="divide-y">
          <template v-for="(group, idx) in selectedShipmentReportingGroups" :key="group">
            <li v-if="idx > 0" class="px-3 py-1.5 text-sm font-semibold">{{ group.name }}</li>
          </template>
        </ul>
      </PopoverTag>
      <FormKit v-model="hiddenRgs" type="hidden" name="reporting_groups" />
    </div>
  </div>
</template>
